import React, { useState } from 'react';
import { Link, router } from '@inertiajs/react';
import SignupLayout from '../Layout.jsx';
import { useDraftAutosave } from '../../../hooks/useDraftAutosave.js';
import { labelClass, inputClass, primaryBtnClass, secondaryBtnClass, ShieldIcon, saveDraftPatch, checkEmailAvailable } from '../shared.jsx';

export default function GroupBuyer({ buyer: initialBuyer, progress }) {
  const [buyer, setBuyer] = useState({ first_name: '', last_name: '', email: '', ...(initialBuyer || {}) });
  const [submitting, setSubmitting] = useState(false);
  const [emailTaken, setEmailTaken] = useState(false);

  useDraftAutosave('group.buyer', buyer);

  const canAdvance = buyer.first_name?.trim() && buyer.last_name?.trim() && /.+@.+\..+/.test(buyer.email || '');

  const handleContinue = async () => {
    if (!canAdvance || submitting) return;
    setSubmitting(true);
    setEmailTaken(false);

    // Persist the draft (sets the cookie if first save) so the check endpoint is authorized.
    await saveDraftPatch({ flow: 'group', group: { buyer } });

    const { available } = await checkEmailAvailable(buyer.email);
    if (!available) {
      setEmailTaken(true);
      setSubmitting(false);
      return;
    }

    router.visit('/signup/group/facility');
  };

  return (
    <SignupLayout progress={progress}>
      <div className="space-y-4">
        <h2 className="text-2xl/9 font-bold tracking-tight text-gray-900">Let's get started</h2>
        <p className="text-sm text-gray-600">Enter your name and email so we can get you started.</p>

        <div className="space-y-3 mt-6">
          <div>
            <label className={labelClass}>First Name <span className="text-red-500">*</span></label>
            <input className={inputClass} value={buyer.first_name} onChange={(e) => setBuyer({ ...buyer, first_name: e.target.value })} placeholder="Enter your first name" />
          </div>
          <div>
            <label className={labelClass}>Last Name <span className="text-red-500">*</span></label>
            <input className={inputClass} value={buyer.last_name} onChange={(e) => setBuyer({ ...buyer, last_name: e.target.value })} placeholder="Enter your last name" />
          </div>
          <div>
            <label className={labelClass}>Email <span className="text-red-500">*</span></label>
            <input
              type="email"
              className={inputClass}
              value={buyer.email}
              onChange={(e) => { setBuyer({ ...buyer, email: e.target.value }); if (emailTaken) setEmailTaken(false); }}
              placeholder="you@example.com"
              autoComplete="email"
            />
            {emailTaken && (
              <p className="mt-2 text-sm text-red-700">
                An account with this email already exists.{' '}
                <Link href={`/login?email=${encodeURIComponent(buyer.email)}`} className="font-semibold underline hover:text-red-800">
                  Sign in instead
                </Link>
                .
              </p>
            )}
          </div>
        </div>

        <div className="mt-4 rounded-md bg-indigo-50 border border-indigo-100 p-3 flex items-start gap-2">
          <ShieldIcon />
          <p className="text-xs text-indigo-800">We'll use this email to send your order confirmation and create your account after purchase.</p>
        </div>

        <div className="flex justify-between gap-3 mt-6">
          <button type="button" onClick={() => router.visit('/signup')} className={secondaryBtnClass}>Back</button>
          <button type="button" onClick={handleContinue} disabled={!canAdvance || submitting} className={primaryBtnClass}>
            {submitting ? 'Saving…' : 'Continue'}
          </button>
        </div>
      </div>
    </SignupLayout>
  );
}
